import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  Divider,
  Stack,
  useMediaQuery,
  useTheme,
  IconButton,
  Tooltip,
  Menu,
  alpha,
  LinearProgress,
  Avatar,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import Grid from '@mui/material/Grid2';
import {
  CalendarToday as CalendarIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  School as SchoolIcon,
  Visibility as ViewIcon,
  GetApp as ExportIcon,
  Refresh as RefreshIcon,
  FileDownload as FileDownloadIcon,
  PictureAsPdf as PdfIcon,
  TableChart as ExcelIcon,
  Assessment as AssessmentIcon,
  Class as ClassIcon,
  PersonPin as PersonPinIcon,
  ExpandMore as ExpandMoreIcon,
  Analytics as AnalyticsIcon
} from '@mui/icons-material';
import { SubjectService } from '../../services/subjectService';
import { AttendanceService } from '../../services/attendanceService';
import { ExportService } from '../../services/exportService';
import { Subject, Student } from '../../types';
import { useAuth } from '../../contexts/AuthContext';

interface SubjectAttendanceStats {
  subjectId: string;
  subjectName: string;
  subjectCode: string;
  totalSessions: number;
  averageAttendanceRate: number;
  totalStudents: number;
  highPerformers: number; // Students with >85% attendance
  lowPerformers: number; // Students with <75% attendance
  attendanceData: {
    date: string;
    presentCount: number;
    absentCount: number;
    attendanceRate: number;
    markedBy: string;
    markedByName: string;
    markedAt: Date;
    students: {
      id: string;
      name: string;
      rollNumber: string;
      status: 'Present' | 'Absent';
      remarks: string;
    }[];
  }[];
}

interface StudentPerformance {
  studentId: string;
  studentName: string;
  rollNumber: string;
  subjectId: string;
  subjectName: string;
  subjectCode: string;
  totalSessions: number;
  presentSessions: number;
  attendanceRate: number;
}

export function SubjectWiseAttendance() {
  const { user } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [subjects, setSubjects] = useState<Subject[]>([]);
  const [selectedSubject, setSelectedSubject] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');
  const [subjectStats, setSubjectStats] = useState<SubjectAttendanceStats[]>([]);
  const [overallStats, setOverallStats] = useState({
    totalSubjects: 0,
    totalSessions: 0,
    overallAttendanceRate: 0,
    totalStudents: 0
  });
  const [loading, setLoading] = useState(false);
  const [detailDialog, setDetailDialog] = useState(false);
  const [selectedSubjectDetail, setSelectedSubjectDetail] = useState<SubjectAttendanceStats | null>(null);
  const [exportMenuAnchor, setExportMenuAnchor] = useState<null | HTMLElement>(null);
  const [exporting, setExporting] = useState(false);
  const [studentPerformance, setStudentPerformance] = useState<StudentPerformance[]>([]);

  useEffect(() => {
    loadSubjects();
  }, []);

  useEffect(() => {
    if (subjects.length > 0) {
      loadSubjectAttendanceStats();
    }
  }, [subjects, selectedSubject, startDate, endDate]);

  const loadSubjects = async () => {
    try {
      setLoading(true);
      const result = await SubjectService.getSubjects(100);
      setSubjects(result.data);
    } catch (error) {
      console.error('Error loading subjects:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSubjectAttendanceStats = async () => {
    try {
      setLoading(true);
      const targetSubjects = selectedSubject === 'all' ? subjects : subjects.filter(s => s.id === selectedSubject);
      
      const statsPromises = targetSubjects.map(async (subject) => {
        try {
          const result = await AttendanceService.getSubjectAttendanceFromSubjectsCollection(
            subject.id,
            startDate || undefined,
            endDate || undefined
          );

          // Calculate performance metrics
          const studentPerformanceMap = new Map<string, { present: number; total: number }>();
          
          result.attendanceData.forEach(session => {
            session.students.forEach(student => {
              if (!studentPerformanceMap.has(student.id)) {
                studentPerformanceMap.set(student.id, { present: 0, total: 0 });
              }
              const perf = studentPerformanceMap.get(student.id)!;
              perf.total++;
              if (student.status === 'Present') {
                perf.present++;
              }
            });
          });

          let highPerformers = 0;
          let lowPerformers = 0;
          
          studentPerformanceMap.forEach(perf => {
            const rate = perf.total > 0 ? (perf.present / perf.total) * 100 : 0;
            if (rate >= 85) highPerformers++;
            if (rate < 75) lowPerformers++;
          });

          return {
            subjectId: subject.id,
            subjectName: result.subjectName,
            subjectCode: result.subjectCode,
            totalSessions: result.totalSessions,
            averageAttendanceRate: result.averageAttendanceRate,
            totalStudents: subject.students.length,
            highPerformers,
            lowPerformers,
            attendanceData: result.attendanceData
          };
        } catch (error) {
          console.warn(`Failed to get attendance for subject ${subject.id}:`, error);
          return {
            subjectId: subject.id,
            subjectName: subject.name,
            subjectCode: subject.code,
            totalSessions: 0,
            averageAttendanceRate: 0,
            totalStudents: subject.students.length,
            highPerformers: 0,
            lowPerformers: 0,
            attendanceData: []
          };
        }
      });

      const stats = await Promise.all(statsPromises);
      setSubjectStats(stats);

      // Calculate overall stats
      const totalSubjects = stats.length;
      const totalSessions = stats.reduce((sum, s) => sum + s.totalSessions, 0);
      const weightedAttendanceSum = stats.reduce((sum, s) => sum + (s.averageAttendanceRate * s.totalSessions), 0);
      const overallAttendanceRate = totalSessions > 0 ? weightedAttendanceSum / totalSessions : 0;
      const totalStudents = stats.reduce((sum, s) => sum + s.totalStudents, 0);

      setOverallStats({
        totalSubjects,
        totalSessions,
        overallAttendanceRate,
        totalStudents
      });

      // Load detailed student performance for selected subject
      if (selectedSubject !== 'all') {
        await loadStudentPerformance(selectedSubject);
      }

    } catch (error) {
      console.error('Error loading subject attendance stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadStudentPerformance = async (subjectId: string) => {
    try {
      const subject = subjects.find(s => s.id === subjectId);
      if (!subject) return;

      const performancePromises = subject.students.map(async (student) => {
        try {
          const result = await AttendanceService.getStudentSubjectAttendanceFromSubjectsCollection(
            subjectId,
            student.id,
            startDate || undefined,
            endDate || undefined
          );

          return {
            studentId: student.id,
            studentName: student.name,
            rollNumber: student.rollNumber,
            subjectId: result.subjectId,
            subjectName: result.subjectName,
            subjectCode: result.subjectCode,
            totalSessions: result.totalSessions,
            presentSessions: result.presentSessions,
            attendanceRate: result.attendanceRate
          };
        } catch (error) {
          return {
            studentId: student.id,
            studentName: student.name,
            rollNumber: student.rollNumber,
            subjectId: subjectId,
            subjectName: subject.name,
            subjectCode: subject.code,
            totalSessions: 0,
            presentSessions: 0,
            attendanceRate: 0
          };
        }
      });

      const performance = await Promise.all(performancePromises);
      setStudentPerformance(performance);
    } catch (error) {
      console.error('Error loading student performance:', error);
    }
  };

  const handleViewDetails = (subjectStat: SubjectAttendanceStats) => {
    setSelectedSubjectDetail(subjectStat);
    setDetailDialog(true);
  };

  const handleExportClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setExportMenuAnchor(event.currentTarget);
  };

  const handleExportClose = () => {
    setExportMenuAnchor(null);
  };

  const handleExportToPDF = async () => {
    try {
      setExporting(true);
      handleExportClose();
      
      // Format data for export - add missing required fields
      const exportData = subjectStats.flatMap(subject => 
        subject.attendanceData.flatMap(session => 
          session.students.map(student => ({
            id: `${subject.subjectId}-${student.id}-${session.date}`,
            studentId: student.id,
            studentName: student.name,
            rollNumber: student.rollNumber,
            classId: 'unknown',
            className: 'Subject Attendance',
            date: new Date(session.date),
            status: student.status
          }))
        )
      );

      const fileName = selectedSubject === 'all' 
        ? 'all_subjects_attendance_report' 
        : `${subjectStats[0]?.subjectCode}_attendance_report`;
      
      await exportToPDF(exportData, fileName);
    } catch (error) {
      console.error('Error exporting to PDF:', error);
      alert('Failed to export PDF. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const handleExportToExcel = async () => {
    try {
      setExporting(true);
      handleExportClose();
      
      // Format data for export - add missing required fields
      const exportData = subjectStats.flatMap(subject => 
        subject.attendanceData.flatMap(session => 
          session.students.map(student => ({
            id: `${subject.subjectId}-${student.id}-${session.date}`,
            studentId: student.id,
            studentName: student.name,
            rollNumber: student.rollNumber,
            classId: 'unknown',
            className: 'Subject Attendance',
            date: new Date(session.date),
            status: student.status
          }))
        )
      );

      const fileName = selectedSubject === 'all' 
        ? 'all_subjects_attendance_report' 
        : `${subjectStats[0]?.subjectCode}_attendance_report`;
      
      await exportToExcel(exportData, fileName);
    } catch (error) {
      console.error('Error exporting to Excel:', error);
      alert('Failed to export Excel. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  const getAttendanceColor = (rate: number): "primary" | "secondary" | "error" | "info" | "success" | "warning" => {
    if (rate >= 85) return 'success';
    if (rate >= 75) return 'warning';
    if (rate >= 60) return 'info';
    return 'error';
  };

  const getPerformanceIcon = (highPerformers: number, lowPerformers: number, total: number) => {
    const highRatio = total > 0 ? highPerformers / total : 0;
    const lowRatio = total > 0 ? lowPerformers / total : 0;
    
    if (highRatio > 0.7) return <TrendingUpIcon color="success" />;
    if (lowRatio > 0.3) return <TrendingDownIcon color="error" />;
    return <AnalyticsIcon color="info" />;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      weekday: 'short',
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Box>
      <Typography variant="h4" sx={{ mb: 3, fontWeight: 700 }}>
        Subject-wise Attendance Analytics
      </Typography>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <FormControl fullWidth>
              <InputLabel>Select Subject</InputLabel>
              <Select
                value={selectedSubject}
                label="Select Subject"
                onChange={(e) => setSelectedSubject(e.target.value)}
              >
                <MenuItem value="all">All Subjects</MenuItem>
                {subjects.map((subject) => (
                  <MenuItem key={subject.id} value={subject.id}>
                    {subject.code} - {subject.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 3 }}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 2 }}>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={loadSubjectAttendanceStats}
              disabled={loading}
            >
              Refresh
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Overall Statistics Cards */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <SchoolIcon color="primary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {overallStats.totalSubjects}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Subjects
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <CalendarIcon color="info" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" color="info.main">
                    {overallStats.totalSessions}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Sessions
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {overallStats.overallAttendanceRate >= 75 ? (
                  <TrendingUpIcon color="success" sx={{ fontSize: 40 }} />
                ) : (
                  <TrendingDownIcon color="error" sx={{ fontSize: 40 }} />
                )}
                <Box>
                  <Typography 
                    variant="h4" 
                    color={overallStats.overallAttendanceRate >= 75 ? 'success.main' : 'error.main'}
                  >
                    {overallStats.overallAttendanceRate.toFixed(1)}%
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Overall Attendance
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid size={{ xs: 12, md: 3 }}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <PeopleIcon color="secondary" sx={{ fontSize: 40 }} />
                <Box>
                  <Typography variant="h4" color="secondary.main">
                    {overallStats.totalStudents}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Total Students
                  </Typography>
                </Box>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Export Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
        <Button
          variant="contained"
          startIcon={exporting ? <CircularProgress size={16} /> : <ExportIcon />}
          disabled={subjectStats.length === 0 || exporting}
          onClick={handleExportClick}
          sx={{
            borderRadius: 2,
            textTransform: 'none',
            fontWeight: 600,
            px: 3,
            py: 1.5,
          }}
        >
          {exporting ? 'Exporting...' : 'Export Data'}
        </Button>
      </Box>

      {/* Subject Statistics Table */}
      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" sx={{ mb: 3 }}>
          Subject Performance Overview
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : subjectStats.length === 0 ? (
          <Alert severity="info">
            No subject attendance data found for the selected filters.
          </Alert>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Subject</TableCell>
                  <TableCell align="center">Students</TableCell>
                  <TableCell align="center">Sessions</TableCell>
                  <TableCell align="center">Avg. Attendance</TableCell>
                  <TableCell align="center">High Performers</TableCell>
                  <TableCell align="center">Low Performers</TableCell>
                  <TableCell align="center">Performance</TableCell>
                  <TableCell align="center">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {subjectStats.map((subject) => (
                  <TableRow key={subject.subjectId} hover>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {subject.subjectCode}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {subject.subjectName}
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={subject.totalStudents}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={subject.totalSessions}
                        size="small"
                        color="info"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Chip
                        label={`${subject.averageAttendanceRate.toFixed(1)}%`}
                        color={getAttendanceColor(subject.averageAttendanceRate)}
                        size="small"
                      />
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <Chip
                          label={subject.highPerformers}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="textSecondary">
                          (≥85%)
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                        <Chip
                          label={subject.lowPerformers}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                        <Typography variant="caption" color="textSecondary">
                          (&lt;75%)
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell align="center">
                      <Box sx={{ width: 100 }}>
                        <LinearProgress
                          variant="determinate"
                          value={subject.averageAttendanceRate}
                          color={getAttendanceColor(subject.averageAttendanceRate)}
                          sx={{ height: 8, borderRadius: 4 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          onClick={() => handleViewDetails(subject)}
                        >
                          <ViewIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Student Performance Section (only for single subject) */}
      {selectedSubject !== 'all' && studentPerformance.length > 0 && (
        <Paper sx={{ p: 3 }}>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Student Performance Details
          </Typography>
          
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Student</TableCell>
                  <TableCell align="center">Total Sessions</TableCell>
                  <TableCell align="center">Present</TableCell>
                  <TableCell align="center">Absent</TableCell>
                  <TableCell align="center">Attendance Rate</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {studentPerformance
                  .sort((a, b) => b.attendanceRate - a.attendanceRate)
                  .map((student) => (
                    <TableRow key={student.studentId} hover>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            <PersonPinIcon fontSize="small" />
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="medium">
                              {student.studentName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {student.rollNumber}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={student.totalSessions}
                          size="small"
                          variant="outlined"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={student.presentSessions}
                          size="small"
                          color="success"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={student.totalSessions - student.presentSessions}
                          size="small"
                          color="error"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Chip
                          label={`${student.attendanceRate.toFixed(1)}%`}
                          color={getAttendanceColor(student.attendanceRate)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ width: 80 }}>
                          <LinearProgress
                            variant="determinate"
                            value={student.attendanceRate}
                            color={getAttendanceColor(student.attendanceRate)}
                            sx={{ height: 6, borderRadius: 3 }}
                          />
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      )}

      {/* Subject Details Dialog */}
      <Dialog 
        open={detailDialog}
        onClose={() => setDetailDialog(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Subject Details - {selectedSubjectDetail?.subjectCode}
        </DialogTitle>
        <DialogContent>
          {selectedSubjectDetail && (
            <Box>
              <Grid container spacing={2} sx={{ mb: 3 }}>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">
                    Subject Name
                  </Typography>
                  <Typography variant="h6">
                    {selectedSubjectDetail.subjectName}
                  </Typography>
                </Grid>
                <Grid size={6}>
                  <Typography variant="body2" color="textSecondary">
                    Average Attendance Rate
                  </Typography>
                  <Typography variant="h6" color={selectedSubjectDetail.averageAttendanceRate >= 75 ? 'success.main' : 'error.main'}>
                    {selectedSubjectDetail.averageAttendanceRate.toFixed(1)}%
                  </Typography>
                </Grid>
              </Grid>

              <Divider sx={{ my: 2 }} />

              <Typography variant="h6" sx={{ mb: 2 }}>
                Session History
              </Typography>
              
              <List>
                {selectedSubjectDetail.attendanceData.map((session, index) => (
                  <React.Fragment key={index}>
                    <ListItem>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, justifyContent: 'space-between' }}>
                            <Typography variant="body1" fontWeight="medium">
                              {formatDate(session.date)}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <Chip
                                label={`${session.presentCount} Present`}
                                color="success"
                                size="small"
                              />
                              <Chip
                                label={`${session.absentCount} Absent`}
                                color="error"
                                size="small"
                              />
                              <Chip
                                label={`${session.attendanceRate}%`}
                                color={getAttendanceColor(session.attendanceRate)}
                                size="small"
                              />
                            </Box>
                          </Box>
                        }
                        secondary={`Marked by: ${session.markedByName || session.markedBy}`}
                      />
                    </ListItem>
                    {index < selectedSubjectDetail.attendanceData.length - 1 && <Divider />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailDialog(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Export Menu */}
      <Menu
        anchorEl={exportMenuAnchor}
        open={Boolean(exportMenuAnchor)}
        onClose={handleExportClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            minWidth: 200,
            boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
            border: '1px solid rgba(255,255,255,0.2)',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem 
          onClick={handleExportToPDF}
          sx={{ 
            py: 1.5, 
            px: 2,
            '&:hover': { 
              backgroundColor: alpha('#d32f2f', 0.08) 
            }
          }}
        >
          <PdfIcon sx={{ mr: 2, color: '#d32f2f' }} />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Export to PDF
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Formatted attendance report
            </Typography>
          </Box>
        </MenuItem>
        <MenuItem 
          onClick={handleExportToExcel}
          sx={{ 
            py: 1.5, 
            px: 2,
            '&:hover': { 
              backgroundColor: alpha('#2e7d32', 0.08) 
            }
          }}
        >
          <ExcelIcon sx={{ mr: 2, color: '#2e7d32' }} />
          <Box>
            <Typography variant="body2" fontWeight={500}>
              Export to Excel
            </Typography>
            <Typography variant="caption" color="textSecondary">
              Spreadsheet with pivot data
            </Typography>
          </Box>
        </MenuItem>
      </Menu>
    </Box>
  );
}

// Custom export functions
const exportToPDF = async (data: any[], fileName: string) => {
  const jsPDF = (await import('jspdf')).default;
  const autoTable = (await import('jspdf-autotable')).default;

  const doc = new jsPDF();
  
  // Add title
  doc.setFontSize(20);
  doc.text('Subject Attendance Report', 14, 22);
  
  // Add timestamp
  doc.setFontSize(12);
  doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, 32);
  
  // Prepare table data
  const tableData = data.map(record => [
    record.studentId,
    record.studentName,
    record.rollNumber,
    new Date(record.date).toLocaleDateString(),
    record.status === 'present' ? 'Present' : 'Absent'
  ]);

  // Add table
  autoTable(doc, {
    head: [['Student ID', 'Student Name', 'Roll Number', 'Date', 'Status']],
    body: tableData,
    startY: 40,
    styles: {
      fontSize: 10,
      cellPadding: 3,
    },
    headStyles: {
      fillColor: [63, 81, 181],
      textColor: 255,
      fontStyle: 'bold',
    },
    alternateRowStyles: {
      fillColor: [245, 245, 245],
    },
  });

  doc.save(`${fileName}.pdf`);
};

const exportToExcel = async (data: any[], fileName: string) => {
  const XLSX = await import('xlsx');
  
  // Prepare Excel data
  const excelData = data.map(record => ({
    'Student ID': record.studentId,
    'Student Name': record.studentName,
    'Roll Number': record.rollNumber,
    'Date': new Date(record.date).toLocaleDateString(),
    'Status': record.status === 'present' ? 'Present' : 'Absent'
  }));

  // Create workbook and worksheet
  const ws = XLSX.utils.json_to_sheet(excelData);
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, ws, 'Attendance Report');

  // Download the file
  XLSX.writeFile(wb, `${fileName}.xlsx`);
}; 